import { useRouter } from "@tanstack/react-router";
import { useRef } from "react";
import {  GENDER_OPTIONS, MARITAL_STATUS_OPTIONS, RELATION_OPTIONS} from "../../../../common/staticData/options";
import DynamicForm, { type DynamicFormSchema } from "../../../../common/components/DynamicForm/DynamicForm";
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { checkNationalIDNo } from "../../../../common/services/realTimeCheckAPI";
import dayjs from "dayjs";


const DependentForm: React.FC = () => {
    const formRef = useRef<any>(null);
    // const navigate = useNavigate();
    const { state } = useRouter().state.location;
    const record = (state as { record?: any })?.record;
    //console.log("record", record);

    // Handle form value changes - auto-set maritalStatus based on relation
    const handleFormValuesChange = (changedValues: Record<string, unknown>) => {
        if (changedValues.relation !== undefined) {
            const relation = changedValues.relation;
            if (relation === 'Spouse') {
                // Find the maritalStatus option value for "Married"
                const marriedOption = MARITAL_STATUS_OPTIONS.find(option => option.label === 'Married');
                if (marriedOption) {
                    formRef.current?.setFieldValue('maritalStatus', marriedOption.value);
                }
            } else {
                // Clear maritalStatus for other relations (including Son/Daughter)
                formRef.current?.setFieldValue('maritalStatus', undefined);
            }
            // Trigger validation of maritalStatus when relation changes
            setTimeout(() => {
                formRef.current?.getForm().validateFields(['maritalStatus']).catch(() => {});
            }, 100);
        } else if (changedValues.maritalStatus !== undefined) {
            // Trigger validation when maritalStatus is changed to clear errors
            setTimeout(() => {
                formRef.current?.getForm().validateFields(['maritalStatus']).catch(() => {});
            }, 100);
        }
    };
    const policyHolderFormSchema: DynamicFormSchema = {
        layout: 'vertical', // Overall form layout
        // submitButtonText: 'Save User', // Custom text for the submit button
        showResetButton: true, // Show a reset button
        sections: [            
            {
                key: 'parent-contact',
                title: 'Parent Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'name',
                        label: 'Name',
                        type: 'input',
                        disabled: true,
                        colSpan: 12,
                        defaultValue: `${record?.name}`

                    },
                    {
                        name: 'category',
                        label: 'Category',
                        type: 'input',
                        disabled: true,
                        colSpan: 12,
                        defaultValue: `${record?.category}`

                    },
                    {
                        name: 'parentNationalIDNo',
                        label: 'Parent National No',
                        type: 'input',
                        disabled: true,
                        colSpan: 12,
                        defaultValue: `${record?.parentNationalIDNo}`

                    },
                ],
            },
            {
                key: 'policyHolder-personal',
                title: 'Personal Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'companyId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.companyId}`

                    },
                    {
                        name: 'customerId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.customerId}`

                    },
                    {
                        name: 'ParentPolicyHolderId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.ParentPolicyHolderId}`

                    },
                    {
                        name: 'category',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.category}`

                    },
                    {
                        name: 'company',
                        label: 'company',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.companyName}`

                    },
                    {
                        name: 'customer',
                        label: 'customerName',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.customerName}`

                    },                  
                    {
                        name: 'parentNationalIDNo',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `${record?.nationalIDNo}`

                    },
                    //{
                    //    name: 'nameTitle', label: 'Title', placeholder: 'Mr. Ms. Mrs.', required: true, requiredMessage: 'Select title',
                    //    type: 'select',
                    //    options: TITLE_OPTIONS,
                    //    colSpan: 12
                    //},
                    {
                        name: 'firstName', label: 'First Name', type: 'input', required: true, colSpan: 12,
                        pattern: 'AlphabetsWithOutSpaceEngAr',
                        validation: [
                            { max: 100, message: 'First Name cannot be more than 50 characters long.' },
                        ],

                    },
                    {
                        name: 'mName', label: 'Middle Name', type: 'input', colSpan: 12,
                        pattern: 'AlphabetsWithOutSpaceEngAr',
                        validation: [
                            { max: 100, message: 'Middle Name cannot be more than 50 characters long.' },
                        ],
                    },
                    {
                        name: 'lastName', label: 'Last Name', type: 'input', required: true, colSpan: 12,
                        pattern: 'AlphabetsWithOutSpaceEngAr',
                        validation: [
                            { max: 50, message: 'Last Name cannot be more than 50 characters long.' },
                        ],
                    },
                    
                    {
                        name: 'gender',
                        label: 'Gender',
                        required: true, requiredMessage: 'Select gender',
                        type: 'select',
                        options: GENDER_OPTIONS,
                        colSpan: 12,
                    },
                    {
                        name: 'relation',
                        label: 'Relation',
                        type: 'select',
                        required: true,
                        requiredMessage:'Please select relation',
                        options: RELATION_OPTIONS,
                        colSpan: 12,
                    },
                    {
                        name: 'maritalStatus',
                        label: 'Marital Status',
                        //required: true, requiredMessage: 'Select marital status',
                        type: 'select',
                        options: MARITAL_STATUS_OPTIONS,
                        colSpan: 12,
                        disabledWhen: [
                            {
                                field: 'relation',
                                operator: '===',
                                value: 'Spouse'
                            }
                        ],
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {
                                    const relation = formRef.current?.getFieldValue('relation');
                                    
                                    // For Spouse, marital status must be "Married" (value: 2)
                                    //if (relation === 'Spouse' && value !== 2) {
                                    //    return Promise.reject(new Error('Marital Status must be "Married" when Relation is "Spouse".'));
                                    //}
                                    
                                    // For Son or Daughter, if a value is selected, it must be "Single" (value: 1)
                                    if ((relation === 'Son' || relation === 'Daughter')) {
                                        if (value && value !== 1) {
                                            return Promise.reject(new Error('Marital Status must be "Single" when Relation is "Son" or "Daughter".'));
                                        }
                                    }
                                    
                                    return Promise.resolve();
                                },
                            },
                        ],
                    },
                    { 
                        name: 'dob', 
                        label: 'Date of Birth', 
                        type: 'date', 
                        required: true, 
                        colSpan: 12, 
                        disabledDateFunction: '(current) => current && current > dayjs().endOf(\'day\')',
                        validation: [
                            {
                                validator: (_: unknown, value: dayjs.Dayjs) => {
                                    if (!value) return Promise.resolve();
                                    
                                    const relation = formRef.current?.getFieldValue('relation');
                                    const today = dayjs();
                                    const age = today.diff(value, 'year');
                                    
                                    // For Spouse: Age must be between 16 and 68
                                    if (relation === 'Spouse') {
                                        if (age < 16 || age > 68) {
                                            return Promise.reject(new Error('Age must be between 16 and 68 when Relation is "Spouse".'));
                                        }
                                    }
                                    
                                    // For Son or Daughter: Age must be between 0 and 25
                                    if (relation === 'Son' || relation === 'Daughter') {
                                        if (age < 0 || age > 25) {
                                            return Promise.reject(new Error('Age must be between 0 and 25 when Relation is "Son" or "Daughter".'));
                                        }
                                    }
                                    
                                    return Promise.resolve();
                                },
                            },
                        ],
                    },
                    {
                        name: 'nationalIDNo', label: 'National ID No', type: 'input', required: true, requiredMessage: 'National Id# is required', colSpan: 12,
                        validation: [
                            { max: 20, message: 'National ID No cannot be more than 20 characters long.' },
                            {
                                // Async validator for checking National ID No uniqueness
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 14) return Promise.resolve(); // Don't call API if less than 4 chars
                                    try {
                                        const result = await checkNationalIDNo(value, record.companyId, record.id);
                                        if (result.exists) {
                                            return Promise.reject('National ID No already exists in this company.');
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        console.error('Error validating National ID No:', error);
                                        return Promise.reject(error.message || 'Error validating National ID No.');
                                    }

                                },

                            },
                        ],
                    },
                    {
                        name: 'passportNo', label: 'Passport No', type: 'input', colSpan: 12,
                        pattern: 'AlphaNumeric',
                        validation: [
                            { max: 20, message: 'Passport No. cannot be more than 20 characters long.' },
                        ],
                    },
                    {
                        name: 'poBoxNo', label: 'PO Box No', type: 'number', colSpan: 12,
                        validation: [
                            { max: 20, message: 'PO Box No cannot be more than 20 characters long.' },
                        ],
                    },
                ],
            },            
            {
                key: 'policyHolder-contact',
                title: 'Contact Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'mobileNo', label: 'Mobile Number', type: 'input', colSpan: 12,
                        pattern: 'PhoneNumber',
                    },
                    {
                        name: 'phoneNo', label: 'Phone Number', type: 'input', colSpan: 12,
                        pattern: 'PhoneNumber',
                    },
                    {
                        name: 'officePhoneNo', label: 'Office Phone Number', type: 'input', colSpan: 12,
                        pattern: 'PhoneNumber',
                    },
                    {
                        name: 'officeEmail', label: 'Office Email', type: 'input', colSpan: 12,
                        pattern: 'Email',
                    },
                    {
                        name: 'officeFaxNo', label: 'Office Fax', type: 'input', colSpan: 12,
                        pattern: 'OnlyDigits',
                        validation: [
                            { max: 15, message: 'Office Fax Number cannot be more than 15 characters long.' },
                        ],
                    },
                    {
                        name: 'officeJoiningDate', label: 'Office Joining Date', type: 'date', colSpan: 12,
                    },
                ],
            },
            {
                key: 'policyHolder-address',
                title: 'Address Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'countryId',
                        label: 'Country',
                        type: 'select',
                        colSpan: 12, // Full width
                        defaultValue: 104,
                        placeholder: "Select country",
                        required: true,
                        requiredMessage: "Country is required.",
                        showSearch: true, // Enable search functionality
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["country"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: "(apiResponse) => apiResponse.country.map(item => ({ label: item.Name, value: item.Id }))"
                        },
                    },
                    {
                        name: 'cityId',
                        label: 'City',
                        type: 'select',
                        // required: true,
                        // requiredMessage: 'City is required.',
                        showSearch: true, // Enable search functionality
                        colSpan: 12,
                        options: [],
                        dynamicOptions: {
                            url: "/City/SearchByCountryId",
                            method: 'GET',
                            transformData: "(apiResponse) => apiResponse.map(item => ({ label: item.name, value: item.id }))",
                            dependsOn: ['countryId'],
                        },
                        disabledWhen: [
                            {
                                field: 'countryId',
                                operator: 'isFalsy',
                            },
                        ],
                    },                   
                    {
                        name: 'area', label: 'Area', type: 'input',
                        // required: true, 
                        colSpan: 12,
                        pattern: 'AlphaNumericAndSomeSpecialCharEngAr',
                        validation: [
                            { max: 50, message: 'Area cannot be more than 50 characters long.' },
                        ],
                    },
                    {
                        name: 'streetNo', label: 'Street No', type: 'input', colSpan: 12,
                        // required: true,
                        pattern: 'OnlyDigits',
                        validation: [
                            { max: 5, message: 'Street No cannot be more than 5 characters long.' },
                        ],
                    },
                    {
                        name: 'address', label: 'Full Address', type: 'textarea',
                        // required: true, 
                        colSpan: 24,
                        pattern: 'AlphaNumericAndSomeSpecialCharEngAr',
                        validation: [
                            { max: 250, message: 'Address cannot be more than 250 characters long.' },
                        ],
                    },
                ],
            },
            {
                key: 'policyHolder-medical',
                title: 'Medical Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'bloodGroup',
                        label: 'Blood Group',
                        type: 'select',
                        // required: true,
                        colSpan: 12, // Full width
                        placeholder: "Select Blood Group",
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["bloodgroup"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: "(apiResponse) => apiResponse.bloodgroup.map(item => ({ label: item.name, value: item.id }))"
                        },
                    },
                    {
                        name: 'weight',
                        label: 'Weight',
                        type: 'number',
                        colSpan: 6,
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {

                                    if (value > 250) {
                                        return Promise.reject(new Error('Value must be less than or equal to 250'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                        pattern: 'OnlyDecimalNumber',

                    },
                    {
                        name: 'weightUnit', label: 'Weight Unit', type: 'input', colSpan: 6,
                        validation: [
                            { max: 10, message: 'Weight unit cannot be more than 10 characters long.' },
                            {
                                validator: (_, value) => {
                                    if (value && value.includes('.')) {
                                        const decimalPart = value.split('.')[1];
                                        if (decimalPart.length > 2) {
                                            return Promise.reject('Only up to 2 decimal places are allowed.');
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ],
                    },
                    {
                        name: 'heightInFeets', label: 'Height (ft)', type: 'number', colSpan: 6,
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {

                                    if (value > 10) {
                                        return Promise.reject(new Error('Value cannot be greater than 10.'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                        pattern: 'OnlyDecimalNumber',
                    },
                    {
                        name: 'heightInInches', label: 'Height (cm)', type: 'number', colSpan: 6,
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {

                                    if (value > 300) {
                                        return Promise.reject(new Error('Value cannot be greater than 300.'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                        pattern: 'OnlyDecimalNumber',
                    },
                    {
                        name: 'allergyIntolerance', label: 'Allergy Intolerance', type: 'textarea', colSpan: 24,
                        pattern: 'AlphabetsWithSpaceEngAr',
                        validation: [
                            { max: 200, message: 'Allergy Intolerance cannot be more than 200 characters long.' },
                        ],
                    },
                    {
                        name: 'chronicDiseases', label: 'Chronic Diseases', type: 'textarea', colSpan: 24,
                        pattern: 'AlphabetsWithSpaceEngAr',
                        validation: [
                            { max: 200, message: 'Chronic Diseases cannot be more than 200 characters long.' },
                        ],
                    },
                    {
                        name: 'chronicMedications', label: 'Chronic Medications', type: 'textarea', colSpan: 24,
                        validation: [
                            { max: 200, message: 'Chronic Medications cannot be more than 200 characters long.' },
                        ],
                    },
                    //{
                    //    name: 'isFamilyCovered', label: 'Is Family Covered', type: 'switch', colSpan: 12,
                    //},
                ],
            },
            {
                key: 'policyHolder-other',
                title: 'Additional Notes',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'note', label: 'Notes', type: 'textarea', colSpan: 24,
                        pattern: 'AlphaNumericAndSomeSpecialCharEngAr',
                        validation: [
                            { max: 500, message: 'Notes cannot be more than 500 characters long.' },
                        ],
                    },
                ],
            }
        ],
    };

    return (
        <>
            <DynamicForm
                ref={formRef}
                schema={policyHolderFormSchema}
                submitApiUrl={API_ENDPOINTS.PARTICIPANTS.SAVE_POLICYHOLDER_DEPENDENT}
                getByIdApiUrl={API_ENDPOINTS.PARTICIPANTS.GET_POLICYHOLDER_BY_ID}
                updateApiUrl={API_ENDPOINTS.PARTICIPANTS.UPDATE_POLICYHOLDER}
                refreshTableDataKey={QUERY_KEYS.PARTICIPANTS.GET_POLICYHOLDER_DETAIL}
                backUrl="history"
                onValuesChange={handleFormValuesChange}             
            />
        </>
    );
};

export default DependentForm;